import React, { useEffect, useRef, useState } from 'react';
import { ArrowRight, Eye, Brain, Shield, Monitor, ChevronDown, Sun, Moon } from 'lucide-react';

const Home = () => {
  const observerRef = useRef<IntersectionObserver | null>(null);
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [isDarkMode, setIsDarkMode] = useState(true);

  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-fade-in-up');
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );

    const elements = document.querySelectorAll('.fade-in-section');
    elements.forEach((el) => observerRef.current?.observe(el));

    return () => observerRef.current?.disconnect();
  }, []);

  return (
    <div className={`min-h-screen relative transition-all duration-500 ${
      isDarkMode 
        ? 'bg-gradient-to-br from-slate-900 via-emerald-900 to-slate-800' 
        : 'bg-gradient-to-br from-gray-50 via-emerald-50 to-teal-50'
    }`}>
      {/* Theme Toggle Button */}
      <button
        onClick={() => setIsDarkMode(!isDarkMode)}
        className={`fixed top-24 right-6 z-50 p-3 rounded-full backdrop-blur-sm border-2 transition-all duration-300 hover:scale-110 ${
          isDarkMode
            ? 'bg-slate-800/40 border-emerald-400/60 text-emerald-400 hover:bg-slate-700/60'
            : 'bg-white/40 border-emerald-600/60 text-emerald-600 hover:bg-white/60'
        }`}
      >
        {isDarkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
      </button>

      {/* Enhanced grainy texture overlay */}
      <div className={`absolute inset-0 transition-opacity duration-500 ${isDarkMode ? 'opacity-40' : 'opacity-20'}`} style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='1.2' numOctaves='6' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)' opacity='0.6'/%3E%3C/svg%3E")`,
        backgroundSize: '180px 180px'
      }}></div>
      
      {/* Subtle color overlay for depth */}
      <div className={`absolute inset-0 transition-all duration-500 ${
        isDarkMode 
          ? 'bg-gradient-to-b from-emerald-900/20 via-transparent to-slate-900/30'
          : 'bg-gradient-to-b from-emerald-100/30 via-transparent to-gray-100/40'
      }`}></div>
      
      {/* Hero Section */}
      <section className="relative overflow-hidden">
        {/* Enhanced background pattern */}
        <div className={`absolute inset-0 transition-opacity duration-500 ${isDarkMode ? 'opacity-10' : 'opacity-15'}`}>
          <div className="absolute inset-0" style={{
            backgroundImage: isDarkMode 
              ? `radial-gradient(circle at 20% 30%, rgba(16, 185, 129, 0.15) 0%, transparent 60%), 
                 radial-gradient(circle at 80% 70%, rgba(16, 185, 129, 0.1) 0%, transparent 50%),
                 radial-gradient(circle at 40% 80%, rgba(6, 78, 59, 0.2) 0%, transparent 40%)`
              : `radial-gradient(circle at 20% 30%, rgba(16, 185, 129, 0.25) 0%, transparent 60%), 
                 radial-gradient(circle at 80% 70%, rgba(16, 185, 129, 0.2) 0%, transparent 50%),
                 radial-gradient(circle at 40% 80%, rgba(6, 78, 59, 0.15) 0%, transparent 40%)`
          }}></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-6 pt-32 pb-32">
          <div className="text-center fade-in-section">
            {/* Main Title */}
            <h1 className={`text-7xl lg:text-9xl font-thin leading-tight mb-2 drop-shadow-lg transition-colors duration-500 ${
              isDarkMode ? 'text-white' : 'text-gray-900'
            }`} style={{
              fontFamily: 'Georgia, "Times New Roman", Times, serif',
              textShadow: isDarkMode 
                ? '0 0 20px rgba(255,255,255,0.3), 0 0 40px rgba(255,255,255,0.1)'
                : '0 0 20px rgba(0,0,0,0.1), 0 0 40px rgba(0,0,0,0.05)'
            }}>
              Protect<br />
              <span className={`bg-clip-text text-transparent transition-all duration-500 ${
                isDarkMode 
                  ? 'bg-gradient-to-r from-emerald-400 via-teal-300 to-emerald-400'
                  : 'bg-gradient-to-r from-emerald-600 via-teal-600 to-emerald-600'
              }`}>
                Gaming
              </span>
            </h1>
            
            {/* Description */}
            <p className={`text-lg lg:text-xl mb-10 leading-relaxed max-w-4xl mx-auto drop-shadow-md mt-6 transition-colors duration-500 ${
              isDarkMode ? 'text-gray-300' : 'text-gray-700'
            }`} style={{
              textShadow: isDarkMode
                ? '0 0 12px rgba(255,255,255,0.15)'
                : '0 0 12px rgba(0,0,0,0.1)'
            }}>
              AI-powered gaming safety platform that protects children from online threats while preserving the joy of gaming.
            </p>
            
            {/* CTA Button */}
            <div className="flex items-center justify-center">
              <button className={`relative inline-flex items-center justify-center backdrop-blur-sm px-10 py-4 rounded-full font-semibold text-lg border-2 transition-all duration-500 transform hover:scale-105 hover:-translate-y-1 shadow-2xl group overflow-hidden ${
                isDarkMode
                  ? 'bg-slate-800/40 text-white border-emerald-400/60 hover:border-emerald-300'
                  : 'bg-white/40 text-gray-900 border-emerald-600/60 hover:border-emerald-500'
              }`}>
                {/* Subtle inner glow */}
                <div className={`absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700 rounded-full ${
                  isDarkMode
                    ? 'bg-gradient-to-r from-emerald-400/10 via-transparent to-emerald-400/10'
                    : 'bg-gradient-to-r from-emerald-600/10 via-transparent to-emerald-600/10'
                }`}></div>
                
                {/* Elegant shimmer */}
                <div className={`absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-1200 rounded-full ${
                  isDarkMode
                    ? 'bg-gradient-to-r from-transparent via-emerald-300/30 to-transparent'
                    : 'bg-gradient-to-r from-transparent via-emerald-600/30 to-transparent'
                }`}></div>
                
                {/* Button text with refined spacing */}
                <span className="relative z-10 flex items-center space-x-3 drop-shadow-md" style={{
                  textShadow: isDarkMode 
                    ? '0 0 15px rgba(255,255,255,0.2)'
                    : '0 0 15px rgba(0,0,0,0.1)'
                }}>
                  <span>Get Started</span>
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-400 drop-shadow-sm" />
                </span>
                
                {/* Subtle outer ring */}
                <div className={`absolute inset-0 rounded-full border opacity-0 group-hover:opacity-100 group-hover:scale-110 transition-all duration-500 ${
                  isDarkMode ? 'border-emerald-400/30' : 'border-emerald-600/30'
                }`}></div>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="pt-0 pb-2 relative -mt-8">
        <div className="max-w-7xl mx-auto px-6">
          {/* Section Header */}
          <div className="mb-0 fade-in-section">
            <div className={`inline-flex items-center space-x-2 px-4 py-2 rounded-full text-sm font-medium mb-6 shadow-lg mt-8 transition-colors duration-500 ${
              isDarkMode
                ? 'bg-emerald-600/25 text-emerald-200'
                : 'bg-emerald-100/80 text-emerald-800'
            }`}>
              <Shield className="w-4 h-4" />
              <span>Advanced Threat Detection</span>
            </div>
          </div>

          {/* Features Grid */}
          <div className="space-y-12">
            {/* Feature 1 - Computer Vision */}
            <div className="fade-in-section">
              <div className="grid lg:grid-cols-2 gap-12 items-center">
                <div>
                  <h3 className={`text-4xl lg:text-5xl font-bold bg-clip-text text-transparent mb-6 leading-tight transition-all duration-500 ${
                    isDarkMode
                      ? 'bg-gradient-to-b from-emerald-400 to-white'
                      : 'bg-gradient-to-b from-emerald-600 to-gray-900'
                  }`}>
                    Computer Vision Technology
                  </h3>
                  <p className={`text-xl mb-8 drop-shadow-md transition-colors duration-500 ${
                    isDarkMode ? 'text-gray-300' : 'text-gray-700'
                  }`} style={{
                    textShadow: isDarkMode 
                      ? '0 0 10px rgba(255,255,255,0.15)'
                      : '0 0 10px rgba(0,0,0,0.1)'
                  }}>
                    Our advanced AI analyzes visual content in real-time, detecting inappropriate images, suspicious behavior patterns, and potential threats before they reach your child.
                  </p>
                </div>
                <div className="relative">
                  {/* Main tilted container */}
                  <div className="transform rotate-3 hover:rotate-1 transition-transform duration-500">
                    <div className={`backdrop-blur-sm rounded-2xl h-96 flex items-center justify-center border-2 shadow-2xl relative overflow-hidden transition-colors duration-500 ${
                      isDarkMode
                        ? 'bg-slate-800/60 border-emerald-400/40'
                        : 'bg-white/60 border-emerald-600/40'
                    }`}>
                      {/* Animated border glow */}
                      <div className={`absolute inset-0 animate-pulse transition-colors duration-500 ${
                        isDarkMode
                          ? 'bg-gradient-to-r from-emerald-400/20 via-transparent to-emerald-400/20'
                          : 'bg-gradient-to-r from-emerald-600/20 via-transparent to-emerald-600/20'
                      }`}></div>
                      
                      {/* Corner accents */}
                      <div className={`absolute top-4 left-4 w-8 h-8 border-l-2 border-t-2 transition-colors duration-500 ${
                        isDarkMode ? 'border-emerald-400/60' : 'border-emerald-600/60'
                      }`}></div>
                      <div className={`absolute top-4 right-4 w-8 h-8 border-r-2 border-t-2 transition-colors duration-500 ${
                        isDarkMode ? 'border-emerald-400/60' : 'border-emerald-600/60'
                      }`}></div>
                      <div className={`absolute bottom-4 left-4 w-8 h-8 border-l-2 border-b-2 transition-colors duration-500 ${
                        isDarkMode ? 'border-emerald-400/60' : 'border-emerald-600/60'
                      }`}></div>
                      <div className={`absolute bottom-4 right-4 w-8 h-8 border-r-2 border-b-2 transition-colors duration-500 ${
                        isDarkMode ? 'border-emerald-400/60' : 'border-emerald-600/60'
                      }`}></div>
                      
                      <div className="text-center relative z-10">
                        <Eye className={`w-20 h-20 mx-auto mb-4 drop-shadow-lg transition-colors duration-500 ${
                          isDarkMode ? 'text-emerald-400/80' : 'text-emerald-600/80'
                        }`} />
                        <span className={`text-2xl font-bold drop-shadow-md transition-colors duration-500 ${
                          isDarkMode ? 'text-emerald-300/90' : 'text-emerald-700/90'
                        }`}>Computer Vision</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Floating accent elements */}
                  <div className={`absolute -top-2 -right-2 w-4 h-4 rounded-full animate-bounce transition-colors duration-500 ${
                    isDarkMode ? 'bg-emerald-400/60' : 'bg-emerald-600/60'
                  }`}></div>
                  <div className={`absolute -bottom-3 -left-3 w-6 h-6 border-2 rounded-full transition-colors duration-500 ${
                    isDarkMode ? 'border-emerald-400/40' : 'border-emerald-600/40'
                  }`}></div>
                </div>
              </div>
            </div>

            {/* Feature 2 - Language Analysis */}
            <div className="fade-in-section">
              <div className="grid lg:grid-cols-2 gap-12 items-center">
                <div className="order-2 lg:order-1">
                  <div className="relative">
                    {/* Skewed container */}
                    <div className="transform -rotate-2 hover:rotate-0 transition-transform duration-700">
                      <div className="bg-slate-800/60 backdrop-blur-sm rounded-3xl h-96 flex items-center justify-center border-2 border-emerald-400/50 shadow-2xl relative overflow-hidden">
                        {/* Diagonal lines */}
                        <div className="absolute inset-0">
                          <div className="absolute top-0 left-0 w-full h-0.5 bg-gradient-to-r from-transparent via-emerald-400/30 to-transparent transform rotate-12"></div>
                          <div className="absolute bottom-0 right-0 w-full h-0.5 bg-gradient-to-r from-transparent via-emerald-400/30 to-transparent transform -rotate-12"></div>
                        </div>
                        
                        {/* Hexagonal outline */}
                        <div className="absolute inset-8 border border-emerald-400/30 transform rotate-45" style={{clipPath: 'polygon(30% 0%, 70% 0%, 100% 30%, 100% 70%, 70% 100%, 30% 100%, 0% 70%, 0% 30%)'}}></div>
                        
                        <div className="text-center relative z-10">
                          <Brain className="w-20 h-20 text-emerald-400/80 mx-auto mb-4 drop-shadow-lg animate-pulse" />
                          <span className="text-2xl font-bold text-emerald-300/90 drop-shadow-md">AI Processing</span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Orbiting elements */}
                    <div className="absolute top-1/2 left-1/2 w-32 h-32 -translate-x-1/2 -translate-y-1/2 pointer-events-none">
                      <div className="absolute top-0 left-1/2 w-2 h-2 bg-emerald-400/60 rounded-full animate-spin" style={{transformOrigin: '0 64px'}}></div>
                    </div>
                  </div>
                </div>
                <div className="order-1 lg:order-2">
                  <div className={`inline-flex items-center space-x-2 px-4 py-2 rounded-full text-sm font-medium mb-6 shadow-lg transition-colors duration-500 ${
                    isDarkMode
                      ? 'bg-emerald-600/25 text-emerald-200'
                      : 'bg-emerald-100/80 text-emerald-800'
                  }`}>
                    <Brain className="w-4 h-4" />
                    <span>Natural Language Processing</span>
                  </div>
                  <h3 className={`text-4xl lg:text-5xl font-bold bg-clip-text text-transparent mb-6 leading-tight transition-all duration-500 ${
                    isDarkMode
                      ? 'bg-gradient-to-b from-emerald-400 to-white'
                      : 'bg-gradient-to-b from-emerald-600 to-gray-900'
                  }`}>
                    Smart Language Analysis
                  </h3>
                  <p className={`text-xl mb-8 drop-shadow-md transition-colors duration-500 ${
                    isDarkMode ? 'text-gray-300' : 'text-gray-700'
                  }`} style={{
                    textShadow: isDarkMode 
                      ? '0 0 10px rgba(255,255,255,0.15)'
                      : '0 0 10px rgba(0,0,0,0.1)'
                  }}>
                    Advanced NLP algorithms monitor text communications, identifying cyberbullying, predatory behavior, and harmful content across all gaming platforms and chat systems.
                  </p>
                </div>
              </div>
            </div>

            {/* Feature 3 - Protection */}
            <div className="fade-in-section">
              <div className="grid lg:grid-cols-2 gap-12 items-center">
                <div>
                  <div className={`inline-flex items-center space-x-2 px-4 py-2 rounded-full text-sm font-medium mb-6 shadow-lg transition-colors duration-500 ${
                    isDarkMode
                      ? 'bg-emerald-600/25 text-emerald-200'
                      : 'bg-emerald-100/80 text-emerald-800'
                  }`}>
                    <Shield className="w-4 h-4" />
                    <span>Real-time Protection</span>
                  </div>
                  <h3 className={`text-4xl lg:text-5xl font-bold bg-clip-text text-transparent mb-6 leading-tight transition-all duration-500 ${
                    isDarkMode
                      ? 'bg-gradient-to-b from-emerald-400 to-white'
                      : 'bg-gradient-to-b from-emerald-600 to-gray-900'
                  }`}>
                    Instant Threat Response
                  </h3>
                  <p className={`text-xl mb-8 drop-shadow-md transition-colors duration-500 ${
                    isDarkMode ? 'text-gray-300' : 'text-gray-700'
                  }`} style={{
                    textShadow: isDarkMode 
                      ? '0 0 10px rgba(255,255,255,0.15)'
                      : '0 0 10px rgba(0,0,0,0.1)'
                  }}>
                    When threats are detected, our system immediately alerts parents while providing age-appropriate guidance to children, ensuring swift intervention without disrupting gameplay.
                  </p>
                </div>
                <div className="relative">
                  {/* Pentagon-like shape */}
                  <div className="transform rotate-1 hover:-rotate-1 transition-transform duration-600">
                    <div className="bg-slate-800/60 backdrop-blur-sm h-96 flex items-center justify-center border-2 border-emerald-400/40 shadow-2xl relative overflow-hidden" style={{clipPath: 'polygon(0% 20%, 20% 0%, 80% 0%, 100% 20%, 100% 80%, 80% 100%, 20% 100%, 0% 80%)'}}>
                      {/* Pulsing background */}
                      <div className="absolute inset-0 bg-emerald-400/5 animate-pulse"></div>
                      
                      {/* Grid pattern */}
                      <div className="absolute inset-0 opacity-20" style={{
                        backgroundImage: `linear-gradient(rgba(16, 185, 129, 0.3) 1px, transparent 1px),
                                         linear-gradient(90deg, rgba(16, 185, 129, 0.3) 1px, transparent 1px)`,
                        backgroundSize: '20px 20px'
                      }}></div>
                      
                      <div className="text-center relative z-10">
                        <Shield className="w-20 h-20 text-emerald-400/80 mx-auto mb-4 drop-shadow-lg" />
                        <span className="text-2xl font-bold text-emerald-300/90 drop-shadow-md">Protection Shield</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Corner indicators */}
                  <div className="absolute top-0 left-0 w-3 h-3 bg-emerald-400 rounded-full animate-ping"></div>
                  <div className="absolute bottom-0 right-0 w-3 h-3 bg-emerald-400 rounded-full animate-ping" style={{animationDelay: '1s'}}></div>
                </div>
              </div>
            </div>

            {/* Feature 4 - Monitoring */}
            <div className="fade-in-section">
              <div className="grid lg:grid-cols-2 gap-12 items-center">
                <div className="order-2 lg:order-1">
                  <div className="relative">
                    {/* Diamond-like rotation */}
                    <div className="transform rotate-6 hover:rotate-3 transition-transform duration-800">
                      <div className="bg-slate-800/60 backdrop-blur-sm rounded-2xl h-96 flex items-center justify-center border-2 border-emerald-400/50 shadow-2xl relative overflow-hidden">
                        {/* Scanning line effect */}
                        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-emerald-400 to-transparent animate-pulse"></div>
                        <div className="absolute bottom-0 right-0 w-full h-1 bg-gradient-to-r from-transparent via-emerald-400 to-transparent animate-pulse" style={{animationDelay: '0.5s'}}></div>
                        
                        {/* Circuit-like pattern */}
                        <div className="absolute inset-4 border border-emerald-400/20 rounded-lg">
                          <div className="absolute top-2 left-2 w-4 h-4 border-l-2 border-t-2 border-emerald-400/40"></div>
                          <div className="absolute top-2 right-2 w-4 h-4 border-r-2 border-t-2 border-emerald-400/40"></div>
                          <div className="absolute bottom-2 left-2 w-4 h-4 border-l-2 border-b-2 border-emerald-400/40"></div>
                          <div className="absolute bottom-2 right-2 w-4 h-4 border-r-2 border-b-2 border-emerald-400/40"></div>
                          
                          {/* Center cross */}
                          <div className="absolute top-1/2 left-1/2 w-8 h-0.5 bg-emerald-400/30 -translate-x-1/2 -translate-y-1/2"></div>
                          <div className="absolute top-1/2 left-1/2 w-0.5 h-8 bg-emerald-400/30 -translate-x-1/2 -translate-y-1/2"></div>
                        </div>
                        
                        <div className="text-center relative z-10">
                          <Monitor className="w-20 h-20 text-emerald-400/80 mx-auto mb-4 drop-shadow-lg" />
                          <span className="text-2xl font-bold text-emerald-300/90 drop-shadow-md">Live Dashboard</span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Data flow indicators */}
                    <div className="absolute top-1/4 -left-2 w-4 h-4 bg-emerald-400/60 rounded-full animate-bounce"></div>
                    <div className="absolute bottom-1/4 -right-2 w-4 h-4 bg-emerald-400/60 rounded-full animate-bounce" style={{animationDelay: '0.3s'}}></div>
                  </div>
                </div>
                <div className="order-1 lg:order-2">
                  <div className={`inline-flex items-center space-x-2 px-4 py-2 rounded-full text-sm font-medium mb-6 shadow-lg transition-colors duration-500 ${
                    isDarkMode
                      ? 'bg-emerald-600/25 text-emerald-200'
                      : 'bg-emerald-100/80 text-emerald-800'
                  }`}>
                    <Monitor className="w-4 h-4" />
                    <span>Comprehensive Monitoring</span>
                  </div>
                  <h3 className={`text-4xl lg:text-5xl font-bold bg-clip-text text-transparent mb-6 leading-tight transition-all duration-500 ${
                    isDarkMode
                      ? 'bg-gradient-to-b from-emerald-400 to-white'
                      : 'bg-gradient-to-b from-emerald-600 to-gray-900'
                  }`}>
                    Complete Gaming Oversight
                  </h3>
                  <p className={`text-xl mb-8 drop-shadow-md transition-colors duration-500 ${
                    isDarkMode ? 'text-gray-300' : 'text-gray-700'
                  }`} style={{
                    textShadow: isDarkMode 
                      ? '0 0 10px rgba(255,255,255,0.15)'
                      : '0 0 10px rgba(0,0,0,0.1)'
                  }}>
                    Monitor gaming sessions across all devices and platforms with detailed reports, screen time analytics, and behavioral insights to help parents make informed decisions.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-24 relative">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-16 fade-in-section">
            <h2 className={`text-5xl lg:text-7xl font-bold mb-6 drop-shadow-lg transition-colors duration-500 ${
              isDarkMode ? 'text-white' : 'text-gray-900'
            }`} style={{
              textShadow: isDarkMode 
                ? '0 0 20px rgba(255,255,255,0.3), 0 0 40px rgba(255,255,255,0.1)'
                : '0 0 20px rgba(0,0,0,0.1), 0 0 40px rgba(0,0,0,0.05)'
            }}>
              Frequently Asked Questions
            </h2>
            <p className={`text-lg lg:text-xl leading-relaxed max-w-3xl mx-auto drop-shadow-md transition-colors duration-500 ${
              isDarkMode ? 'text-gray-300' : 'text-gray-700'
            }`} style={{
              textShadow: isDarkMode 
                ? '0 0 12px rgba(255,255,255,0.15)'
                : '0 0 12px rgba(0,0,0,0.1)'
            }}>
              Get answers to common questions about SafePlay AI and how we protect your family's gaming experience.
            </p>
          </div>

          <div className="space-y-6 fade-in-section mt-4">
            {[
              {
                question: "How does SafePlay AI protect my child while gaming?",
                answer: "SafePlay AI uses advanced computer vision and natural language processing to monitor gaming content in real-time. It detects inappropriate images, cyberbullying, predatory behavior, and other threats, immediately alerting parents while providing age-appropriate guidance to children."
              },
              {
                question: "Which gaming platforms and devices are supported?",
                answer: "SafePlay AI works across all major gaming platforms including PC (Windows, Mac, Linux), gaming consoles (PlayStation, Xbox, Nintendo Switch), and mobile devices (iOS, Android). It monitors popular games like Fortnite, Minecraft, Roblox, and many others."
              },
              {
                question: "Will my child know they're being monitored?",
                answer: "SafePlay AI operates transparently with age-appropriate disclosure. Children are aware of the safety measures in place, which actually helps them make better decisions. The system focuses on education and protection rather than secretive surveillance."
              },
              {
                question: "How accurate is the threat detection system?",
                answer: "Our AI system maintains over 95% accuracy in threat detection through continuous machine learning and regular updates. False positives are minimized through advanced algorithms, and parents can review and provide feedback on all alerts."
              },
              {
                question: "What happens when a threat is detected?",
                answer: "When a threat is detected, parents receive immediate notifications via their preferred method (email, SMS, or app notification). The system can also take automatic protective actions like pausing the game or blocking communication, depending on your settings."
              },
              {
                question: "Is my family's data secure and private?",
                answer: "Absolutely. SafePlay AI uses end-to-end encryption and follows strict privacy standards including COPPA compliance. Your family's data is never sold or shared with third parties, and you maintain full control over your information with options for local processing."
              }
            ].map((faq, index) => (
              <div key={index} className={`backdrop-blur-sm border rounded-2xl p-6 transition-all duration-300 ${
                isDarkMode
                  ? 'bg-slate-800/30 border-emerald-400/30 hover:border-emerald-400/60 hover:bg-emerald-400/10'
                  : 'bg-white/30 border-emerald-600/30 hover:border-emerald-600/60 hover:bg-emerald-600/10'
              }`}>
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className={`w-full flex items-center justify-center text-center group transition-colors ${
                    isDarkMode ? 'hover:text-emerald-400' : 'hover:text-emerald-600'
                  }`}
                >
                  <h3 className={`text-lg lg:text-xl font-bold transition-colors drop-shadow-md flex-1 ${
                    isDarkMode 
                      ? 'text-white group-hover:text-emerald-400' 
                      : 'text-gray-900 group-hover:text-emerald-600'
                  }`} style={{
                    textShadow: isDarkMode 
                      ? '0 0 10px rgba(255,255,255,0.2)'
                      : '0 0 10px rgba(0,0,0,0.1)',
                    fontFamily: '"Helvetica Neue", "Arial", sans-serif'
                  }}>
                    {faq.question}
                  </h3>
                  <ChevronDown
                    className={`w-6 h-6 transition-all duration-300 ml-4 flex-shrink-0 ${
                      openFaq === index ? 'rotate-180' : ''
                    } ${
                      isDarkMode 
                        ? 'text-gray-400 group-hover:text-emerald-400' 
                        : 'text-gray-600 group-hover:text-emerald-600'
                    }`}
                  />
                </button>
                {openFaq === index && (
                  <div className={`mt-6 pt-6 border-t animate-fade-in-up transition-colors duration-500 ${
                    isDarkMode ? 'border-emerald-400/30' : 'border-emerald-600/30'
                  }`}>
                    <p className={`text-base lg:text-lg leading-relaxed drop-shadow-sm text-center transition-colors duration-500 ${
                      isDarkMode ? 'text-gray-300' : 'text-gray-700'
                    }`} style={{
                      textShadow: isDarkMode 
                        ? '0 0 8px rgba(255,255,255,0.1)'
                        : '0 0 8px rgba(0,0,0,0.05)'
                    }}>
                      {faq.answer}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;